/**
 * Recharge bundle subscribe — Essential Foods, B2C.
 *
 * TWO things live here:
 *
 *  1. window.RechargeBundleEngine — the ONE owner of the Recharge bundle cart
 *     contract (BUNDLES_EXECUTION.md §3/§7 + the dynamic-box markers of
 *     DYNAMIC_BUNDLES_PLAN.md §2.2). It builds /cart/add.js lines for a bundle
 *     instance and posts them. Used by:
 *       • <recharge-bundle> below (FIXED bundles — the product form), and
 *       • sections/section-product-bundle-builder.liquid (DYNAMIC boxes — the
 *         måltidskasser builder).
 *
 *     Each child line carries:
 *       _bundle_product = bundle handle  (rule key, matches the portal)
 *       _bundle_id      = uuid per bundle instance (groups it in Recharge)
 *       _bundle_title, _bundle_freq_label, the visible "Part of the bundle" label
 *       _bundle_unit    = "1/2" / "2/2" (part k of n) on the halves of a split
 *                         single-variant recipe (EF-462, see recipeParts)
 *     and, for DYNAMIC boxes only:
 *       _bundle_slot    = the 1-based slot the child fills
 *       _bundle_variant = the shell variant chosen (the kitchen) → price row
 *
 *  2. <recharge-bundle> — the fixed-bundle widget: on a BUNDLE product page
 *     (e.g. "2,5KG x 4") it adds a one-time / subscription choice.
 *       • One-time ("Køb")  → does nothing; the normal product form submits and
 *                             adds the bundle product as today.
 *       • Subscribe         → intercepts the add, and instead POSTs the bundle's
 *                             individual CHILD variants, each with the selected
 *                             selling plan, so Recharge subscribes them. The
 *                             bundle product itself is never added.
 *     The page's quantity selector is respected: N = N bundle instances, each a
 *     separate _bundle_id group at recipe quantities. Recipe + frequencies are
 *     rendered by snippets/recharge-bundle-subscribe.liquid.
 */
(function () {
  if (window.RechargeBundleEngine) return;

  function newBundleId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return "rb_" + Date.now().toString(36) + Math.random().toString(36).slice(2);
  }

  /**
   * EF-462 — the cart lines one bundle instance becomes: [{ item, quantity, unit? }].
   *
   * A subscribed recipe made of ONE variant (e.g. "2,5KG x 4") would be a single
   * subscription line, and Shopify's checkout then prints that line's plan price ×
   * quantity as "Subtotal for gentagne opkrævninger" (1.156 kr) — it ignores the
   * bundle discount and the portal's recurring reprice (895 kr). With two or more
   * subscription lines it prints a generic sentence instead, as it already does
   * for taste boxes. So that recipe is split into two even lines (4 → 2 + 2,
   * 3 → 2 + 1), kept apart by `_bundle_unit` so Shopify does not merge them.
   *
   * Downstream: the discount function sums quantities per variant across lines;
   * the Rebuy cart grouping (custom.js) shows the halves as one row; the portal
   * keeps the two Recharge subscriptions and prices them as ONE bundle, each part
   * owning its share of the recipe (subscription portal app/lib/split.ts — it splits
   * exactly like this: as even as possible, the remainder on the lowest part).
   * Dynamic boxes (slots) and one-time purchases are never split.
   */
  function recipeParts(o) {
    const items = o.items || [];
    const parts = items.map((item) => ({ item: item, quantity: parseInt(item.quantity, 10) || 1 }));
    if (!o.subscribe) return parts;
    if (items.some((item) => item.slot !== undefined && item.slot !== null && item.slot !== "")) return parts;

    const ids = new Set(items.map((item) => parseInt(item.id, 10)).filter(Boolean));
    if (ids.size !== 1) return parts;
    const total = parts.reduce((sum, p) => sum + p.quantity, 0);
    if (total < 2) return parts;

    const halves = [Math.ceil(total / 2), Math.floor(total / 2)];
    return halves.map((quantity, i) => ({
      item: items[0],
      quantity: quantity,
      unit: i + 1 + "/" + halves.length,
    }));
  }

  /**
   * Build the /cart/add.js `items` for `count` instances of a bundle.
   * @param {Object} o
   * @param {string}  o.handle         bundle product handle (_bundle_product)
   * @param {string}  [o.title]        bundle display name (_bundle_title + visible label)
   * @param {string}  [o.partOfLabel]  localized "Part of the bundle" key for the visible property
   * @param {boolean} o.subscribe      true → attach selling plans + subscription markers
   * @param {string}  [o.groupName]    chosen selling-plan GROUP NAME (frequency)
   * @param {string}  [o.freqLabel]    its localized label (_bundle_freq_label + _SUBSCRIPTION/RECURRING)
   * @param {Array}   o.items          [{ id, quantity, plans: {groupName: planId}, slot? }]
   *                                   (a dynamic option's EXTRA variant is its own item with the same slot)
   * @param {string|number} [o.shellVariantId]  DYNAMIC boxes: the shell variant (_bundle_variant)
   * @param {number}  [o.count=1]      how many instances (each gets its own _bundle_id)
   */
  function buildLines(o) {
    const count = o.count && o.count > 0 ? o.count : 1;
    const parts = recipeParts(o);
    const lines = [];
    for (let n = 0; n < count; n++) {
      const bundleId = newBundleId();
      parts.forEach(({ item, quantity, unit }) => {
        const id = parseInt(item.id, 10);
        if (!id) return;
        const line = {
          id: id,
          quantity: quantity,
          properties: {
            // Hidden (underscore) subscription marker, inserted FIRST so it is the
            // top line when staff open the order line's properties.
            ...(o.subscribe && o.freqLabel ? { "_SUBSCRIPTION/RECURRING": o.freqLabel } : {}),
            _bundle_product: o.handle,
            _bundle_id: bundleId,
          },
        };
        if (unit) line.properties._bundle_unit = unit;
        if (o.title) line.properties._bundle_title = o.title;
        if (o.partOfLabel && o.title) line.properties[o.partOfLabel] = o.title;
        // DYNAMIC boxes (måltidskasser): which slot this child fills + the price row.
        if (item.slot !== undefined && item.slot !== null && item.slot !== "") {
          line.properties._bundle_slot = String(item.slot);
        }
        if (o.shellVariantId) line.properties._bundle_variant = String(o.shellVariantId);
        // Attach THIS child's own plan id for the chosen group (per-child).
        if (o.subscribe && o.groupName && item.plans) {
          const planId = item.plans[o.groupName];
          if (planId) line.selling_plan = planId;
          if (o.freqLabel) line.properties._bundle_freq_label = o.freqLabel;
        }
        lines.push(line);
      });
    }
    return lines;
  }

  /** POST the lines. Resolves { ok, data } (never rejects on HTTP errors). */
  function addLines(items) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: items }),
    }).then((res) => res.json().then((data) => ({ ok: res.ok, data })));
  }

  /**
   * Refresh + open the drawer the way the Broadcast theme does after its own add:
   * <cart-items>.getCart() re-fetches the cart section, rebuilds the drawer, and
   * dispatches "theme:product:added" which <cart-drawer> listens to.
   */
  function refreshCart() {
    const cartItems = document.querySelector("cart-items");
    if (cartItems && typeof cartItems.getCart === "function") {
      cartItems.getCart();
    } else {
      const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart_url) || "/cart";
      window.location = cartUrl;
    }
  }

  /**
   * Frequencies shared by EVERY product in `plansList` (each a {groupName: planId}
   * map), matched by group NAME — the store-wide rule (bundles_faq/subscribable-bundles.md).
   * Order = first map's order. Empty list → [].
   */
  function sharedGroupNames(plansList) {
    if (!plansList || plansList.length === 0) return [];
    const first = Object.keys(plansList[0] || {});
    return first.filter((name) => plansList.every((p) => p && p[name]));
  }

  window.RechargeBundleEngine = { newBundleId, buildLines, addLines, refreshCart, sharedGroupNames };
})();

if (!customElements.get("recharge-bundle")) {
  class RechargeBundle extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.handle || "";
      this.formId = this.dataset.formId || "";
      this.form = this.formId ? document.getElementById(this.formId) : null;
      this.items = this.readItems();
      this._busy = false;

      // Translated strings from Liquid (with "translation missing" guards).
      // bundleTitle: shown at checkout as the visible "Part of the bundle" value
      // AND carried as a stable hidden _bundle_title for the Recharge portal.
      this.bundleTitle = this.dataset.bundleTitle || "";
      this.partOfLabel = this.cleanStr(this.dataset.partOfLabel, "Part of the bundle");
      this.subscribeLabel = this.cleanStr(this.dataset.subscribeLabel, "Subscribe");
      this.errorText = this.cleanStr(this.dataset.errorText, "Couldn’t add the bundle to cart. Please try again.");

      // Bind once so add/removeEventListener pair up.
      this.onSubmitCapture = this.onSubmitCapture.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onPurchaseChange = this.onPurchaseChange.bind(this);

      // Purchase-type radios toggle the frequency select + button label.
      this.querySelectorAll("[data-rb-purchase]").forEach((radio) => {
        radio.addEventListener("change", this.onPurchaseChange);
      });
      this.onPurchaseChange();

      // Mark the widget "touched" on the first real selection change, so unchecked
      // radio rings shift from soapstone to mid-grey (mirrors Recharge).
      this.addEventListener("change", () => this.classList.add("rb-touched"), { once: true });

      // Intercept the add when "subscribe" is active. Cover both the native
      // form submit (Enter / main button) and clicks on any add-to-cart button
      // (e.g. a sticky bar button that doesn't submit the form).
      if (this.form) {
        this.form.addEventListener("submit", this.onSubmitCapture, true);
      }
      document.addEventListener("click", this.onClickCapture, true);
    }

    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener("submit", this.onSubmitCapture, true);
      }
      document.removeEventListener("click", this.onClickCapture, true);
    }

    readItems() {
      const tag = this.querySelector("[data-rb-items]");
      if (!tag) return [];
      try {
        const parsed = JSON.parse(tag.textContent || "[]");
        return Array.isArray(parsed) ? parsed.filter((i) => i && i.id) : [];
      } catch (e) {
        return [];
      }
    }

    cleanStr(value, fallback) {
      if (!value || value.indexOf("translation missing") === 0) return fallback;
      return value;
    }

    showError(message) {
      const el = this.querySelector("[data-rb-error]");
      if (!el) return;
      el.textContent = message || this.errorText;
      el.hidden = false;
    }

    clearError() {
      const el = this.querySelector("[data-rb-error]");
      if (!el) return;
      el.textContent = "";
      el.hidden = true;
    }

    get isSubscribe() {
      const checked = this.querySelector("[data-rb-purchase]:checked");
      return checked ? checked.value === "subscribe" : false;
    }

    get selectedGroupId() {
      // The frequency <select> value is the selling-plan GROUP NAME (e.g. "4 week
      // subscription") — products don't share group ids, so we key frequencies by
      // name; each child resolves its OWN plan id for that name (see item.plans).
      const select = this.querySelector("[data-rb-plan]");
      return select && select.value ? select.value : null;
    }

    get selectedFreqLabel() {
      // The visible (already-localized) text of the chosen frequency option,
      // carried onto each line so the cart card shows the same localized label.
      const select = this.querySelector("[data-rb-plan]");
      if (!select || select.selectedIndex < 0) return "";
      const opt = select.options[select.selectedIndex];
      return opt ? opt.textContent.trim() : "";
    }

    onPurchaseChange() {
      const freq = this.querySelector("[data-rb-frequency]");
      if (freq) freq.hidden = !this.isSubscribe;
      this.updateButtonLabel();
    }

    updateButtonLabel() {
      const strings = (window.theme && window.theme.strings) || {};
      const label = this.isSubscribe ? this.subscribeLabel : strings.addToCart || "Add to cart";
      this.addButtons().forEach((btn) => {
        const text = btn.querySelector(".btn__text");
        if (text) text.textContent = label;
      });
    }

    addButtons() {
      return [...document.querySelectorAll("button[data-add-to-cart]")];
    }

    onSubmitCapture(event) {
      if (!this.isSubscribe) return; // let the bundle product add normally
      event.preventDefault();
      event.stopImmediatePropagation();
      this.subscribeAddToCart();
    }

    onClickCapture(event) {
      if (!this.isSubscribe) return;
      const btn = event.target.closest("button[data-add-to-cart]");
      if (!btn) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.subscribeAddToCart();
    }

    getBundleQuantity() {
      // Mirror the page's quantity selector: N in the selector = N bundles.
      const input = this.form && this.form.querySelector('[name="quantity"]');
      const q = input ? parseInt(input.value, 10) : 1;
      return q && q > 0 ? q : 1;
    }

    buildPayload() {
      // Each chosen bundle is its OWN instance: a fresh _bundle_id, with the
      // children at their recipe quantities (the engine does this per `count`).
      return window.RechargeBundleEngine.buildLines({
        handle: this.handle,
        title: this.bundleTitle,
        partOfLabel: this.partOfLabel,
        subscribe: this.isSubscribe,
        groupName: this.selectedGroupId,
        freqLabel: this.selectedFreqLabel,
        items: this.items,
        count: this.getBundleQuantity(),
      });
    }

    subscribeAddToCart() {
      if (this._busy) return; // de-dupe click + submit firing together
      const items = this.buildPayload();
      if (!items.length) return;

      this._busy = true;
      this.clearError();
      this.setLoading(true);

      window.RechargeBundleEngine.addLines(items)
        .then(({ ok, data }) => {
          this._busy = false;
          this.setLoading(false);
          if (!ok) {
            // Surface a user-facing message (e.g. a child is out of stock).
            console.warn("[recharge-bundle] add failed", data);
            this.showError((data && data.description) || this.errorText);
            return;
          }
          window.RechargeBundleEngine.refreshCart();
        })
        .catch((err) => {
          this._busy = false;
          this.setLoading(false);
          console.warn("[recharge-bundle] add error", err);
          this.showError(this.errorText);
        });
    }

    setLoading(on) {
      this.addButtons().forEach((btn) => {
        btn.classList.toggle("loading", on);
        btn.toggleAttribute("disabled", on);
      });
    }
  }

  customElements.define("recharge-bundle", RechargeBundle);
}
